<template>
  <PublicShell :color="data && data.brand ? data.brand.color : null" narrow>
    <div class="wd-card">
      <div v-if="loading" role="status" class="wd-loading"><span class="pb-sr-only">Loading…</span><span class="pb-skeleton wd-sk"></span></div>
      <div v-else-if="failed" class="pb-alert is-error pub-failed" role="alert"><i class="mdi mdi-wifi-off pb-ico" aria-hidden="true"></i><span>We couldn't open this page. Check your connection and try again.</span><button type="button" class="pb-button sm outline" @click="reload">Try again</button></div>
    <template v-else-if="gone">
        <span class="pb-empty-icon" aria-hidden="true"><i class="mdi mdi-link-variant-off"></i></span>
        <h1 class="wd-title display-font">This link isn't valid</h1>
        <p class="pb-body">{{ gone }}</p>
      </template>
      <template v-else-if="data">
        <p class="wd-brand"><strong>{{ data.brand.name }}</strong></p>
        <h1 class="wd-title display-font">Your feedback</h1>
        <blockquote class="wd-quote">{{ data.text }}</blockquote>

        <div v-if="data.withdrawn" class="pb-alert is-success" role="status"><i class="mdi mdi-check-circle-outline pb-ico" aria-hidden="true"></i>
          <span>It's withdrawn. {{ data.brand.name }} can no longer publish it<template v-if="wasPublished">, and it leaves their widgets and wall within a minute</template>.</span></div>
        <template v-else>
          <div v-if="data.details_hidden" class="pb-alert is-success" role="status"><i class="mdi mdi-check-circle-outline pb-ico" aria-hidden="true"></i>
            <span>Your name, job title, company and photo are hidden. {{ data.published ? 'The words stay published.' : '' }}</span></div>
          <p class="pb-body">{{ data.published ? 'It is published on ' + possessive(data.brand.name) + ' website' + (data.shown.length ? ', with your ' + listText(data.shown) : '') + '.' : 'It isn\'t published right now.' }}
            You can change your mind at any time:</p>
          <div v-if="!confirm" class="wd-options">
            <button type="button" class="wd-option" @click="confirm = 'all'"><i class="mdi mdi-delete-outline" aria-hidden="true"></i>
              <span><strong>Remove it entirely</strong><span class="pb-muted">{{ data.brand.name }} may no longer publish your words.</span></span></button>
            <button v-if="!data.details_hidden" type="button" class="wd-option" @click="confirm = 'details'"><i class="mdi mdi-account-off-outline" aria-hidden="true"></i>
              <span><strong>Keep the words, hide my name, job title, company and photo</strong><span class="pb-muted">It shows as "A customer of {{ data.brand.name }}".</span></span></button>
          </div>
          <div v-else class="wd-confirm">
            <p class="pb-body"><strong>{{ confirm === 'all' ? 'Remove your feedback from everything public?' : 'Hide your name, job title, company and photo?' }}</strong> This can't be undone from here.</p>
            <div class="pb-actions"><button type="button" class="wd-go" :disabled="busy" @click="withdraw">{{ busy ? 'Saving…' : 'Yes, ' + (confirm === 'all' ? 'remove it' : 'hide my details') }}</button>
              <button type="button" class="pb-button ghost" @click="confirm = ''">Cancel</button></div>
          </div>
          <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
        </template>
      </template>
    </div>
    <template #footer>TrustClap stores this feedback on {{ data && data.brand ? possessive(data.brand.name) : possessive('the business') }} behalf. <a :href="privacyUrl">Privacy policy</a></template>
  </PublicShell>
</template>

<script>
import PublicShell from '../components/PublicShell.vue';
import { get, post, message } from '../api';
import { listText, possessive } from '../lib/format';

export default {
  name: 'PublicWithdrawView',
  components: { PublicShell },
  data: () => ({ data: null, loading: true, gone: '', failed: false, confirm: '', busy: false, error: '', wasPublished: false }),
  computed: {
    token() { return String(this.$route.params.token || ''); },
    privacyUrl() { return ((this.$config && this.$config.LANDING_PAGE_URL) || '') + '/privacy'; }
  },
  async mounted() {
    try { this.data = await get('/public/withdraw/' + encodeURIComponent(this.token)); } catch (error) {
      const status = error.response ? error.response.status : 0;
      if (status === 404 || status === 429) this.gone = message(error, 'This link is not valid any more. Contact the business directly.');
      else this.failed = true;
    } finally { this.loading = false; }
  },
  methods: {
    listText,
    possessive,
    reload() { window.location.reload(); },
    async withdraw() {
      this.busy = true;
      this.error = '';
      try {
        // Only a published testimonial has public pages to leave.
        this.wasPublished = !!this.data.published;
        this.data = await post('/public/withdraw/' + encodeURIComponent(this.token), { scope: this.confirm });
        this.confirm = '';
      } catch (error) { this.error = message(error); } finally { this.busy = false; }
    }
  }
};
</script>

<style scoped>
.wd-card{background:#fff;border:1px solid #E6DED2;border-radius:20px;padding:32px}
.wd-brand{margin:0 0 14px;font-size:16px;color:#1F2328}
.wd-title{font:500 30px/1.2 var(--font-display)!important;margin:0 0 16px}
.wd-quote{margin:0 0 18px;padding:16px 18px;border-left:3px solid var(--brand);background:#FBF8F3;border-radius:0 12px 12px 0;
  font:500 17px/1.55 var(--font-display)!important;color:#1F2328;white-space:pre-line}
.wd-options{display:grid;gap:10px;margin-top:14px}
.wd-option{display:flex;gap:12px;align-items:flex-start;text-align:left;padding:14px 16px;border:1.5px solid #E6DED2;border-radius:14px;background:#fff;cursor:pointer;min-height:44px}
.wd-option:hover{border-color:var(--brand-line)}
.wd-option .mdi{font-size:22px;color:var(--brand-text)}
.wd-option>span{display:flex;flex-direction:column;gap:2px;font-size:15px;color:#1F2328}
.wd-confirm{margin-top:14px}
.wd-go{min-height:44px;padding:10px 18px;border:0;border-radius:10px;background:var(--brand);color:var(--brand-on);font:600 14.5px var(--font-body);cursor:pointer}
.wd-go:focus-visible{outline:2px solid #1F2328;outline-offset:2px}
.wd-loading .wd-sk{display:block;height:160px}
@media(max-width:600px){.wd-card{padding:22px 16px}}
</style>
